import React, { useEffect, useMemo, useState } from "react";
import TextField from "@mui/material/TextField";

interface TimeFieldProps {
    value: string;
    label?: string;
    onBlur?: (e: string) => void;
    error?: boolean;
    helperText?: string;
    required?: boolean;
    size?: "small" | "medium";
    disabled?: boolean;
}

const normalizeTimeInput = (rawValue: string): string | null => {
    const trimmed = (rawValue ?? "").trim();
    if (!trimmed) return "";

    const cleaned = trimmed.replace(".", ":");
    const parts = cleaned.split(":");
    if (parts.length > 2) return null;

    const hoursPart = parts[0];
    const minutesPart = parts.length === 2 ? parts[1] : "";
    if (!/^\d{1,2}$/.test(hoursPart)) return null;
    if (minutesPart && !/^\d{1,2}$/.test(minutesPart)) return null;

    const hours = Number.parseInt(hoursPart, 10);
    const minutes = minutesPart ? Number.parseInt(minutesPart, 10) : 0;

    if (hours === 24 && minutes === 0) return "24:00";
    if (hours < 0 || hours > 23) return null;
    if (minutes < 0 || minutes > 59) return null;

    return `${hours}:${minutes.toString(10).padStart(2, "0")}`;
};

export const MuiTimeField = ({
    value,
    onBlur,
    error,
    helperText,
    required,
    label,
    size,
    disabled,
}: TimeFieldProps) => {
    const normalizedPropValue = useMemo(() => {
        const normalized = normalizeTimeInput(value);
        return normalized ?? (value ?? "");
    }, [value]);

    const [textValue, setTextValue] = useState<string>(normalizedPropValue);
    const [lastValidValue, setLastValidValue] = useState<string>(normalizedPropValue);

    useEffect(() => {
        setTextValue(normalizedPropValue);
        setLastValidValue(normalizedPropValue);
    }, [normalizedPropValue]);

    return (
        <TextField
            fullWidth
            label={label}
            value={textValue}
            size={size}
            disabled={disabled}
            onChange={(e) => setTextValue(e.target.value)}
            onBlur={() => {
                const normalized = normalizeTimeInput(textValue);
                if (normalized === null) {
                    setTextValue(lastValidValue);
                    return;
                }
                setTextValue(normalized);
                setLastValidValue(normalized);
                onBlur?.(normalized);
            }}
            helperText={helperText}
            error={!!error}
            required={required}
            inputProps={{
                inputMode: "numeric",
                pattern: "[0-9:.]*",
                placeholder: "HH:mm",
                maxLength: 5,
            }}
        />
    );
};
